'use strict';

import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import {
	Color, DepthFormat,
	DepthTexture, FloatType, Frustum, Group, LinearSRGBColorSpace, Matrix4, Mesh, MeshBasicMaterial,
	NearestFilter,
	NoBlending,
	ShaderMaterial, SphereGeometry,
	UniformsUtils, Vector3,
	WebGLRenderTarget,
} from 'three';
import { SSAODepthShader } from 'three/examples/jsm/shaders/SSAOShader';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import vertex from './Shaders/DayNight/vertex.glsl';
import fragment from './Shaders/DayNight/fragment.glsl';
import { Effect } from './Effect';


class DayNightPass extends Pass{
	constructor (camera,width,height) {
		super();

		this.camera = camera;
		this.width = width;
		this.height = height;

		this.depthRenderMaterial = new ShaderMaterial( {
			defines: Object.assign( {}, SSAODepthShader.defines ),
			uniforms: UniformsUtils.clone( SSAODepthShader.uniforms ),
			vertexShader: SSAODepthShader.vertexShader,
			fragmentShader: SSAODepthShader.fragmentShader,
			blending: NoBlending
		});

		this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near;
		this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far;

		this.defaultMaterial = new ShaderMaterial({
			uniforms: UniformsUtils.clone( CopyShader.uniforms ),
			vertexShader: CopyShader.vertexShader,
			fragmentShader: CopyShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});

		this.dayNightMaterial = new ShaderMaterial({
			defines: {
				POINTS_LIGHT_COUNT: 1,
			},
			uniforms: {
				tDiffuse: { value: null },
				tDepth: { value: null },
				tDepthRGB: {value: null},
				tSky: { value: null },

				lights: {
					value: [{
							position: new Vector3(),
							color: new Color(),
							rotation: new Vector3(),
							intensity: 0,
							radius: 0
						}]
				},

				fogNear: {value: 0.673},
				fogFar: {value: 0.37},
				uProjectionMatrix: {value: this.camera.projectionMatrix},
				uMatrixWorldInverse: {value: this.camera.matrixWorldInverse},

				nightLut: {value: null},
				nightLutSize: {value: 0},
				nightLutIntensity: {value: 0},

				dayLut: {value: null},
				dayLutSize: {value: 0.0},
				dayLutIntensity: {value: 0.0},

				sunriseTime: {value: 0.0},
				sunsetTime: {value: 0.0},
				currentTime: {value: 0.0},
				dayStartTime: {value: 0.0},
				dayEndTime: {value: 0.0},
				uTime: {value: 0.0}
			},
			vertexShader: vertex,
			fragmentShader: fragment,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});

		this.sky = null;

		this.uniforms = this.dayNightMaterial.uniforms;

		this.defaultRt = new WebGLRenderTarget(this.width,this.height);
		this.depthRt = new WebGLRenderTarget(this.width,this.height);
		this.skyRt = new WebGLRenderTarget(this.width,this.height);

		this.fsQuad = new FullScreenQuad(null);
		this.originalClearColor = new Color();
		this.needsSwap = false;
	}

	setDayTime(times) {
		this.dayNightMaterial.uniforms.sunriseTime.value = times.sunrise;
		this.dayNightMaterial.uniforms.sunsetTime.value = times.sunset;
		this.dayNightMaterial.uniforms.dayStartTime.value = times.dayStart;
		this.dayNightMaterial.uniforms.dayEndTime.value = times.dayEnd;
	}

	setTime(time) {
		this.dayNightMaterial.uniforms.currentTime.value = time;
	}

	setSize( width, height) {
		this.width = width;
		this.height = height;
		this.defaultRt.setSize(width,height);
		this.depthRt.setSize(width,height);
		this.skyRt.setSize(width,height);
	}

	tick(delta) {
		if (this.enabled && this.sky && delta) {
			this.sky.material.uniforms.uTime.value += delta;
			this.dayNightMaterial.uniforms.uTime.value += delta;
		}
	}

	setSky(sky) {
		this.sky = sky;
	}

	setLights(arrayOfLights) {
		this.dayNightMaterial.defines.POINTS_LIGHT_COUNT = arrayOfLights.length;
		this.dayNightMaterial.uniforms.lights.value = arrayOfLights;
		this.dayNightMaterial.needsUpdate = true;
	}

	render( renderer, writeBuffer, readBuffer) {
		this.dayNightMaterial.uniforms.uProjectionMatrix.value = this.camera.projectionMatrix;
		this.dayNightMaterial.uniforms.uMatrixWorldInverse.value = this.camera.matrixWorldInverse;
		this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near;
		this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far;

		//RenderSky
		if (this.sky) {
			renderer.setRenderTarget(this.skyRt);
			renderer.render(this.sky,this.camera);
			this.dayNightMaterial.uniforms[ 'tSky' ].value = this.skyRt.texture;
			renderer.setRenderTarget(null);
		}

		//Render beauty
		this.defaultMaterial.uniforms.tDiffuse.value = readBuffer.texture;
		this.renderPass(renderer,this.defaultMaterial,this.defaultRt);
		// Depth
		this.depthRenderMaterial.uniforms.tDepth.value = readBuffer.depthTexture;
		this.renderPass(renderer,this.depthRenderMaterial,this.depthRt);

		this.dayNightMaterial.uniforms[ 'tDiffuse' ].value = this.defaultRt.texture;
		this.dayNightMaterial.uniforms[ 'tDepthRGB' ].value = readBuffer.depthTexture;
		this.dayNightMaterial.uniforms[ 'tDepth' ].value = this.depthRt.texture;

		this.renderPass( renderer, this.dayNightMaterial, this.renderToScreen ? null : readBuffer );
	}

	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;

		renderer.setRenderTarget( renderTarget );

		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {

			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();

		}

		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );

		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );

	}
}


export class DayNightEffect extends Effect{
	constructor (camera) {
		super();
		this.effect = new DayNightPass(camera,window.innerWidth,window.innerHeight);
		this.active = false;
	}

	setSky(sky) {
		this.effect.setSky(sky);
	}

	setDayTime(times) {
		this.effect.setDayTime(times);
	}

	setTime(time) {
		this.effect.setTime(time);
	}

	get config() {

	}

	set config(conf) {

	}
}
